import DOMPurify from 'dompurify';

/**
 * sanitizeHtml — client-side defense-in-depth for rich-text bodies (article /
 * material `body_html`, lesson text blocks, and the rich task-text fields in the
 * block editor) that we render via dangerouslySetInnerHTML.
 *
 * The courses-service already sanitizes body_html on the way in, but we never
 * trust HTML that reaches dangerouslySetInnerHTML: a second, independent pass in
 * the browser (DOMPurify) means a gap in the server sanitizer — or HTML that
 * arrived through some other path — still can't execute script in the student's
 * session. Always run user/admin-authored HTML through this before rendering.
 */

// Inline `style` is allowed (the rich editor emits `color` for text colour) but
// filtered down to a tiny safe set of properties — no positioning, no url(), no
// anything that could break layout or exfiltrate. Everything else is dropped.
const SAFE_STYLE_PROP = /^(color|background-color|font-weight|font-style|text-decoration)$/i;

/**
 * Белая заливка текста выбрасывается.
 *
 * ⚠️ ЗАЧЕМ. Текст уроков часто вставляют копированием из Word или Google Docs, а
 * оттуда каждая строка приходит с `background-color: #ffffff`. На белой странице
 * это незаметно, и годами лежало незамеченным. 02.09.2026 кабинет ученика стал
 * песочным — и заказчик увидел белые прямоугольники вокруг каждой строки урока.
 *
 * Выбрасываем ИМЕННО белое: жёлтая или другая заливка — это осмысленное
 * выделение куратора, его трогать нельзя.
 */
const WHITE_BG = /^(#fff|#ffffff|white|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(,\s*[\d.]+\s*)?\))$/i;

function isWhiteBackground(decl: string): boolean {
  const [prop, ...rest] = decl.split(':');
  if ((prop ?? '').trim().toLowerCase() !== 'background-color') return false;
  return WHITE_BG.test(rest.join(':').trim());
}

let hookInstalled = false;
function installStyleHook() {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return;
    data.attrValue = data.attrValue
      .split(';')
      .map((decl) => decl.trim())
      .filter((decl) => SAFE_STYLE_PROP.test((decl.split(':')[0] ?? '').trim()))
      .filter((decl) => !isWhiteBackground(decl))
      .join('; ');
  });
}

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  installStyleHook();
  return DOMPurify.sanitize(html, {
    // Drop any element that could execute script or break out of the content
    // flow. DOMPurify already strips on* handlers and javascript: URLs by
    // default; we additionally forbid framing/SVG/MathML vectors and the <style>
    // ELEMENT (the style ATTRIBUTE stays, filtered to SAFE_STYLE_PROP above, so
    // rich-text colours survive).
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math'],
    USE_PROFILES: { html: true },
  });
}
